import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  onAuthStateChanged,
  applyActionCode,
  checkActionCode,
  signOut,
  sendEmailVerification,
} from "firebase/auth";
import { logEvent } from "firebase/analytics";
import { auth, db, analyticsReady } from "./firebase";
import { postFunctionJson } from "./utils/functionHttp";
import { doc, getDoc } from "firebase/firestore";
import {
  cacheUserProfile,
  clearCachedUserProfile,
  deriveFlagsFromProfile,
  deriveUserFlags,
  getCachedUserProfile,
  getEmailVerificationActionCodeSettings,
  getPostAuthRoute,
  isPasswordProviderUser,
  isUserVerified,
  mustVerifyEmailAuthOnly,
  profileLooksMeaningful,
} from "./auth/authState";
import {
  ACTIVE_CUSTOMER_COMPANY_EVENT,
  clearStoredActiveCustomerCompany,
  hydrateCustomerProfileWithMemberships,
  loadCustomerMemberships,
} from "./auth/customerMemberships";
import { getFeatureAccessMap, mergeFeatureFlags } from "./utils/featureAccess";

// ✅ Notifications Provider
import { NotificationsProvider } from "./components/notifications/NotificationsProvider";

// Layout
import AppLayout from "./layout/AppLayout";

// Public auth-related screens
import Login from "./components/auth/login/Login";
import SignUp from "./components/auth/signup/SignUp";
import InvitedUserDetails from "./screens/InvitedUserDetails";
import CompleteAccountSetup from "./screens/CompleteAccountSetup";

// Main app screens
import Dashboard from "./screens/Dashboard";
import SampleOrdersScreen from "./screens/SampleOrdersScreen";
import OrdersScreen from "./screens/OrdersScreen";
import AccountsScreen from "./screens/AccountsScreen";
import InventoryScreen from "./screens/InventoryScreen";
import ProfileScreen from "./screens/ProfileScreen";
import UsersScreen from "./screens/UsersScreen";
import TeamScreen from "./screens/TeamScreen";
import CustomerSampleScreen from "./screens/CustomerSampleScreen";
import TerritoriesMapScreen from "./screens/TerritoriesMapScreen";
import AdminScreen from "./screens/AdminScreen";
import NotificationsScreen from "./screens/NotificationsScreen";
import DeveloperToolsScreen from "./screens/DeveloperToolsScreen";
import TaskScreen from "./screens/TaskScreen";
import DocumentsScreen from "./screens/DocumentsScreen"; // ✅ NEW
import GridsScreen from "./screens/GridsScreen";
import { canViewGrids } from "./utils/gridsAccess";

// Public feedback form (from email link)
import FeedbackForm from "./screens/FeedbackForm";

// Online presence hook (global)
import useOnlinePresence from "./hooks/useOnlinePresence";

/** Full-screen overlay that hides "route flashes" */
const BootOverlay = ({ message = "Loading…" }) => {
  return (
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-sky-950 via-slate-950 to-slate-950 flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white/10 border border-white/10 rounded-2xl p-6 backdrop-blur-md shadow-2xl">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full border-2 border-white/40 border-t-white animate-spin" />
          <div className="text-white">
            <div className="text-sm font-semibold">Please wait</div>
            <div className="text-xs text-white/75 mt-0.5">{message}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ✅ CRITICAL FIX: First-login OAuth race hardening
 * After signInWithPopup, onAuthStateChanged fires immediately, often before /users/{uid} is written.
 * This helper retries briefly so your app doesn't get stuck with userProfile=null forever.
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeUserStatus = (value) => (value || "").toString().trim().toLowerCase();

const isDeactivatedUserProfile = (profile) => {
  if (!profile) return false;
  if (profile.disabled === true) return true;
  const status = normalizeUserStatus(profile.status);
  return ["inactive", "disabled", "deactivated"].includes(status);
};

async function getUserProfileDocWithRetry(uid, { maxWaitMs = 7000 } = {}) {
  const ref = doc(db, "users", uid);

  const start = Date.now();
  let attempt = 0;
  let delay = 150;

  while (Date.now() - start < maxWaitMs) {
    attempt += 1;

    try {
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = snap.data() || {};
        if (profileLooksMeaningful(data)) {
          console.log(`[App] ✅ userProfile found (attempt ${attempt})`);
          return snap;
        }

        // doc exists but looks incomplete — brief retry for eventual consistency / multi-write flows
        console.warn(
          `[App] userProfile exists but looks incomplete (attempt ${attempt}). Retrying...`,
          data
        );
      } else {
        console.warn(`[App] userProfile not found yet (attempt ${attempt}). Retrying...`);
      }
    } catch (err) {
      console.error(`[App] getUserProfile attempt ${attempt} failed:`, err);
      if (err?.code === "permission-denied") throw err; // retries won't fix rules
    }

    await sleep(delay);
    delay = Math.min(Math.floor(delay * 1.35), 650);
  }

  console.warn("[App] ❌ userProfile retry window exhausted; returning null");
  return null;
}

const VerifyEmailScreen = ({ user }) => {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const autoContinueHandledRef = useRef(false);

  const email = user?.email || "";

  useEffect(() => {
    let cancelled = false;

    const autoContinueIfVerified = async () => {
      if (!auth.currentUser || autoContinueHandledRef.current) return;

      try {
        await auth.currentUser.reload();
        if (cancelled || !auth.currentUser?.emailVerified) return;

        autoContinueHandledRef.current = true;
        setMessage("Email verified! Logging you in...");

        const userRef = doc(db, "users", auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};

        if (cancelled) return;
        const flags = deriveFlagsFromProfile(userData);
        navigate(getPostAuthRoute(flags), { replace: true });
      } catch (err) {
        console.warn("[VerifyEmailScreen] auto verification check skipped:", err);
      }
    };

    autoContinueIfVerified();
    const timer = window.setInterval(autoContinueIfVerified, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [navigate]);

  const resend = async () => {
    setError("");
    setMessage("");

    if (!auth.currentUser) {
      setError("No authenticated user found. Please log in again.");
      return;
    }

    setSending(true);
    try {
      await sendEmailVerification(auth.currentUser, getEmailVerificationActionCodeSettings());
      setMessage("Verification email sent. Check your inbox (and spam).");
    } catch (err) {
      console.error("[VerifyEmailScreen] sendEmailVerification failed:", err);
      setError(err?.message || "Failed to send verification email.");
    } finally {
      setSending(false);
    }
  };

  const check = async () => {
    setError("");
    setMessage("");

    if (!auth.currentUser) {
      setError("No authenticated user found. Please log in again.");
      return;
    }

    setChecking(true);
    try {
      await auth.currentUser.reload();
      if (!auth.currentUser.emailVerified) {
        setError("Still not verified. Click the link in your email, then try again.");
        return;
      }

      setMessage("Email verified! Logging you in...");

      // ✅ IMPORTANT: After verification, redirect based on user state
      const userRef = doc(db, "users", auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};

      const flags = deriveFlagsFromProfile(userData);
      navigate(getPostAuthRoute(flags), { replace: true });
    } catch (err) {
      console.error("[VerifyEmailScreen] reload/check failed:", err);
      setError(err?.message || "Could not check verification status.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-900 via-slate-900 to-slate-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white/95 rounded-2xl shadow-2xl shadow-sky-900/40 border border-slate-200/70 backdrop-blur-sm p-6 sm:p-8">
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-2">Verify your email</h1>

        <p className="text-xs sm:text-sm text-slate-600 mb-4">
          We need to verify <span className="font-semibold">{email}</span> before you can access staff areas.
        </p>

        {message && (
          <div className="mb-3 text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            {message}
          </div>
        )}

        {error && (
          <div className="mb-3 text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <button
            type="button"
            onClick={resend}
            disabled={sending}
            className="w-full bg-sky-600 hover:bg-sky-700 text-white py-2.5 rounded-full text-sm font-semibold shadow-lg shadow-sky-500/30 transition-colors disabled:opacity-60"
          >
            {sending ? "Sending…" : "Resend verification email"}
          </button>

          <button
            type="button"
            onClick={check}
            disabled={checking}
            className="w-full border border-slate-300 bg-white hover:bg-slate-50 text-slate-900 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {checking ? "Checking…" : "I verified — continue"}
          </button>

          <button
            type="button"
            onClick={() => signOut(auth)}
            className="w-full text-xs text-slate-500 hover:text-slate-700 mt-2"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

const VerifyEmailRoute = ({ user, redirectAfterLogin, gateTo, profileSufficient, initializing }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search || "");
  const mode = params.get("mode");
  const oobCode = params.get("oobCode");
  const isEmailVerification = mode === "verifyEmail" && !!oobCode;

  if (isEmailVerification) {
    return (
      <LoginWithVerificationHandler
        user={user}
        redirectAfterLogin={redirectAfterLogin}
        gateTo={gateTo}
        profileSufficient={profileSufficient}
        initializing={initializing}
      />
    );
  }

  if (initializing) return <RouteBootShell />;
  if (!user) return <Navigate to="/login" replace />;
  if (isUserVerified(user)) return <Navigate to={redirectAfterLogin()} replace />;

  return <VerifyEmailScreen user={user} />;
};

// ✅ Updated RequireAuth that checks verification for ALL routes
const RouteBootShell = () => <div className="min-h-screen bg-slate-950" />;

const RequireAuth = ({ user, gateTo, children, allowUnverified = false, initializing = false }) => {
  const location = useLocation();

  if (initializing) {
    return <RouteBootShell />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // ✅ CRITICAL: Block ALL access for unverified password auth users
  if (!allowUnverified && mustVerifyEmailAuthOnly(user)) {
    return <Navigate to="/verify-email" replace />;
  }

  if (gateTo) {
    return <Navigate to={gateTo} replace state={{ from: location }} />;
  }

  return children;
};

// ✅ Allow unverified access only to specific routes
const isAllowedUnverifiedRoute = (path) => {
  const allowedPaths = ["/verify-email", "/login", "/signup", "/sample-feedback", "/invited-user"];
  return allowedPaths.some((allowed) => path.startsWith(allowed));
};

const CompleteAccountSetupRoute = ({ user, gateTo, initializing }) => {
  const location = useLocation();

  if (initializing) {
    return <RouteBootShell />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // ✅ Block access to setup if not verified (for password auth users)
  if (mustVerifyEmailAuthOnly(user)) {
    return <Navigate to="/verify-email" replace />;
  }

  if (gateTo) {
    return <Navigate to={gateTo} replace state={{ from: location }} />;
  }

  const params = new URLSearchParams(location.search || "");
  const mode = params.get("mode") || "onboard";

  return <CompleteAccountSetup mode={mode} />;
};

const LoginWithVerificationHandler = ({ user, redirectAfterLogin, gateTo, profileSufficient, initializing }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const params = new URLSearchParams(location.search || "");
  const mode = params.get("mode");
  const oobCode = params.get("oobCode");
  const isEmailVerification = mode === "verifyEmail" && !!oobCode;
  const justVerified = params.get("verified") === "1";

  useEffect(() => {
    if (!isEmailVerification) return;

    let cancelled = false;

    const verify = async () => {
      try {
        setStatus("verifying");

        try {
          if (auth.currentUser) {
            console.log("[LoginWithVerificationHandler] Signing out existing user before verification...");
            await signOut(auth);
          }
        } catch (signOutErr) {
          console.warn("[LoginWithVerificationHandler] signOut error (ignoring):", signOutErr);
        }

        if (cancelled) return;

        try {
          await checkActionCode(auth, oobCode);
        } catch (precheckErr) {
          const code = precheckErr?.code || "";
          const currentUserAlreadyVerified = auth.currentUser?.emailVerified === true;
          if (code === "auth/invalid-action-code" && currentUserAlreadyVerified) {
            console.warn(
              "[LoginWithVerificationHandler] Verification code already used; current user is already verified."
            );
            setStatus("idle");
            navigate("/login?verified=1", { replace: true });
            return;
          }
          throw precheckErr;
        }

        if (cancelled) return;

        console.log("[LoginWithVerificationHandler] Applying email verification code...");
        await applyActionCode(auth, oobCode);

        if (cancelled) return;

        console.log("[LoginWithVerificationHandler] Email verified. Redirecting to clean /login…");
        setStatus("idle");
        navigate("/login?verified=1", { replace: true });
      } catch (err) {
        console.error("[LoginWithVerificationHandler] Error during email verification:", err);
        if (cancelled) return;

        const code = err?.code || "";
        const currentUserAlreadyVerified = auth.currentUser?.emailVerified === true;
        if (code === "auth/invalid-action-code" && currentUserAlreadyVerified) {
          console.warn(
            "[LoginWithVerificationHandler] Verification link is stale, but the user is already verified."
          );
          setStatus("idle");
          navigate("/login?verified=1", { replace: true });
          return;
        }

        setStatus("error");
        setErrorMessage(
          err?.message || "This verification link is invalid or has expired. Please request a new email."
        );
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [isEmailVerification, oobCode, navigate]);

  if (isEmailVerification) {
    if (status === "verifying") return <div className="min-h-screen bg-slate-950" />;

    if (status === "error") {
      return (
        <div className="min-h-screen bg-gradient-to-br from-sky-900 via-slate-900 to-slate-950 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white/95 rounded-2xl shadow-2xl shadow-sky-900/40 border border-slate-200/70 backdrop-blur-sm p-6 sm:p-8">
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-2">
              We couldn't verify your email
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mb-3">{errorMessage}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white py-2.5 rounded-full text-sm font-semibold shadow-lg shadow-sky-500/30 transition-colors"
            >
              Back to login
            </button>
          </div>
        </div>
      );
    }

    return <div className="min-h-screen bg-slate-950" />;
  }

  if (justVerified) {
    if (initializing) return <RouteBootShell />;
    if (user && gateTo) return <Navigate to={gateTo} replace />;
    if (user && profileSufficient) return <Navigate to={redirectAfterLogin()} replace />;
    if (!user) return <Login />;

    return <RouteBootShell />;
  }

  if (initializing) return <RouteBootShell />;

  if (user && gateTo) return <Navigate to={gateTo} replace />;

  if (user && !gateTo && !profileSufficient) {
    return <RouteBootShell />;
  }

  if (user) return <Navigate to={redirectAfterLogin()} replace />;

  return <Login />;
};

const CLIENT_ERROR_LOG_ENDPOINT =
  "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/logClientError";

function App() {
  const location = useLocation();
  const path = location.pathname || "";

  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [companyFeatureFlags, setCompanyFeatureFlags] = useState({});
  const [developerFeatureFlags, setDeveloperFeatureFlags] = useState({});
  const [companyBillingState, setCompanyBillingState] = useState({
    planType: null,
    subscriptionActive: null,
    stripeSubscriptionId: "",
    seatLimits: null,
  });
  const [featureFlagsReady, setFeatureFlagsReady] = useState(false);

  const [overlayVisible, setOverlayVisible] = useState(false);
  const overlayHoldUntilRef = useRef(0);
  const recentClientErrorKeysRef = useRef(new Map());

  // ✅ prevents stale async loads from older auth events overwriting current state
  const authChangeIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    analyticsReady.then((analytics) => {
      if (cancelled || !analytics || typeof window === "undefined") return;

      logEvent(analytics, "page_view", {
        page_title: document.title || "Supply Hawk Admin",
        page_location: window.location.href,
        page_path: `${location.pathname}${location.search}${location.hash}`,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    console.log("[App] Subscribing to onAuthStateChanged");

    let mounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const authChangeId = ++authChangeIdRef.current;

      setInitializing(true);

      console.log(
        "[App] onAuthStateChanged firebaseUser:",
        firebaseUser
          ? {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              emailVerified: firebaseUser.emailVerified,
              isPasswordAuth: isPasswordProviderUser(firebaseUser),
            }
          : null
      );

      if (firebaseUser) {
        try {
          await firebaseUser.reload();
          console.log("[App] firebaseUser.reload() complete:", {
            uid: firebaseUser.uid,
            emailVerified: firebaseUser.emailVerified,
          });
        } catch (e) {
          console.warn("[App] firebaseUser.reload() failed (ignoring):", e);
        }
      }

      if (!mounted || authChangeId !== authChangeIdRef.current) return;

      setUser(firebaseUser || null);

      if (!firebaseUser) {
        setUserProfile(null);
        clearCachedUserProfile();
        clearStoredActiveCustomerCompany();
        setInitializing(false);
        return;
      }

      const storedProfile = getCachedUserProfile(firebaseUser.uid);

      try {
        console.log("[App] Fetching userProfile from Firestore for uid:", firebaseUser.uid);

        // ✅ FIX: retry briefly to avoid OAuth first-login race
        const snap = await getUserProfileDocWithRetry(firebaseUser.uid, { maxWaitMs: 7000 });

        if (!mounted || authChangeId !== authChangeIdRef.current) return;

        if (snap && snap.exists()) {
          const data = snap.data();
          console.log("[App] Loaded userProfile doc (JSON):", JSON.stringify(data, null, 2));

          const firestoreLooksIncomplete =
            data && !data.role && !data.companyId && !data.planType && Object.keys(data).length <= 3;

          let merged;
          if (
            firestoreLooksIncomplete &&
            storedProfile &&
            (storedProfile.role || storedProfile.companyId || storedProfile.planType)
          ) {
            console.warn("[App] Firestore doc looks incomplete; using cached profile to avoid bad redirects.");
            merged = { ...data, ...storedProfile };
          } else {
            merged = storedProfile ? { ...storedProfile, ...data } : data;
          }

          let hydratedProfile = merged;
          if ((merged?.role || "").toString().trim().toLowerCase() === "customer") {
            try {
              const memberships = await loadCustomerMemberships(db, firebaseUser.uid);
              hydratedProfile = hydrateCustomerProfileWithMemberships({
                uid: firebaseUser.uid,
                profile: merged,
                memberships,
              });
            } catch (membershipErr) {
              console.warn("[App] Failed to load customer memberships:", membershipErr);
            }
          }

          if (isDeactivatedUserProfile(hydratedProfile)) {
            console.warn("[App] User profile is inactive/disabled. Signing out:", {
              uid: firebaseUser.uid,
              status: hydratedProfile?.status || null,
              disabled: hydratedProfile?.disabled === true,
            });
            clearCachedUserProfile();
            clearStoredActiveCustomerCompany();
            setUser(null);
            setUserProfile(null);
            await signOut(auth).catch((err) => {
              console.warn("[App] Failed to sign out inactive user:", err);
            });
            return;
          }

          setUserProfile(hydratedProfile);
          cacheUserProfile(hydratedProfile);
        } else {
          console.warn("[App] No userProfile doc found after retry window for uid:", firebaseUser.uid);

          // If we have cached profile, use it; otherwise keep null.
          if (storedProfile) {
            if (isDeactivatedUserProfile(storedProfile)) {
              console.warn("[App] Cached user profile is inactive/disabled. Signing out:", {
                uid: firebaseUser.uid,
                status: storedProfile?.status || null,
                disabled: storedProfile?.disabled === true,
              });
              clearCachedUserProfile();
              clearStoredActiveCustomerCompany();
              setUser(null);
              setUserProfile(null);
              await signOut(auth).catch((err) => {
                console.warn("[App] Failed to sign out inactive cached user:", err);
              });
              return;
            }
            setUserProfile(storedProfile);
          } else {
            setUserProfile(null);

            // ✅ extra safety: one more delayed re-fetch (non-blocking) to avoid rare timing edge cases
            setTimeout(async () => {
              if (!mounted) return;
              if (authChangeId !== authChangeIdRef.current) return;
              try {
                console.log("[App] Delayed re-fetch of userProfile (safety retry)...");
                const safetySnap = await getUserProfileDocWithRetry(firebaseUser.uid, { maxWaitMs: 5000 });
                if (!mounted || authChangeId !== authChangeIdRef.current) return;
                if (safetySnap && safetySnap.exists()) {
                  const safetyData = safetySnap.data() || {};
                  console.log("[App] Safety retry loaded userProfile:", JSON.stringify(safetyData, null, 2));
                  let hydratedSafetyData = safetyData;
                  if ((safetyData?.role || "").toString().trim().toLowerCase() === "customer") {
                    try {
                      const memberships = await loadCustomerMemberships(db, firebaseUser.uid);
                      hydratedSafetyData = hydrateCustomerProfileWithMemberships({
                        uid: firebaseUser.uid,
                        profile: safetyData,
                        memberships,
                      });
                    } catch (membershipErr) {
                      console.warn("[App] Failed to load safety retry customer memberships:", membershipErr);
                    }
                  }
                  if (isDeactivatedUserProfile(hydratedSafetyData)) {
                    console.warn("[App] Safety retry profile is inactive/disabled. Signing out:", {
                      uid: firebaseUser.uid,
                      status: hydratedSafetyData?.status || null,
                      disabled: hydratedSafetyData?.disabled === true,
                    });
                    clearCachedUserProfile();
                    clearStoredActiveCustomerCompany();
                    setUser(null);
                    setUserProfile(null);
                    await signOut(auth).catch((err) => {
                      console.warn("[App] Failed to sign out inactive safety retry user:", err);
                    });
                    return;
                  }
                  setUserProfile(hydratedSafetyData);
                  cacheUserProfile(hydratedSafetyData);
                }
              } catch (e) {
                console.warn("[App] Safety retry failed:", e);
              }
            }, 900);
          }
        }
      } catch (err) {
        console.error("[App] Error loading user profile:", err);

        // On rules issues, don't keep spinning forever silently.
        if (err?.code === "permission-denied") {
          console.error("[App] permission-denied reading /users/{uid}. Check Firestore rules for self-read.");
        }

        const storedProfile2 = getCachedUserProfile(firebaseUser.uid);
        if (isDeactivatedUserProfile(storedProfile2)) {
          console.warn("[App] Fallback cached profile is inactive/disabled. Signing out:", {
            uid: firebaseUser.uid,
            status: storedProfile2?.status || null,
            disabled: storedProfile2?.disabled === true,
          });
          clearCachedUserProfile();
          clearStoredActiveCustomerCompany();
          setUser(null);
          setUserProfile(null);
          await signOut(auth).catch((signOutErr) => {
            console.warn("[App] Failed to sign out inactive fallback user:", signOutErr);
          });
          return;
        }
        setUserProfile(storedProfile2 || null);
      } finally {
        if (!mounted || authChangeId !== authChangeIdRef.current) return;
        setInitializing(false);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleActiveCompanyChange = async (event) => {
      const targetUid = event?.detail?.uid || "";
      const targetCompanyId = event?.detail?.companyId || "";
      if (!user?.uid || targetUid !== user.uid || !targetCompanyId) return;
      if ((userProfile?.role || "").toString().trim().toLowerCase() !== "customer") return;

      try {
        const memberships = await loadCustomerMemberships(db, user.uid);
        const hydratedProfile = hydrateCustomerProfileWithMemberships({
          uid: user.uid,
          profile: userProfile || {},
          memberships,
        });
        setUserProfile(hydratedProfile);
        cacheUserProfile(hydratedProfile);
      } catch (err) {
        console.warn("[App] Failed to refresh active customer company:", err);
      }
    };

    window.addEventListener(ACTIVE_CUSTOMER_COMPANY_EVENT, handleActiveCompanyChange);
    return () => {
      window.removeEventListener(ACTIVE_CUSTOMER_COMPANY_EVENT, handleActiveCompanyChange);
    };
  }, [user, userProfile]);

  useOnlinePresence(user);

  useEffect(() => {
    const activeCompanyId = userProfile?.companyId || "";
    if (!user) {
      setCompanyFeatureFlags({});
      setDeveloperFeatureFlags({});
      setCompanyBillingState({
        planType: null,
        subscriptionActive: null,
        stripeSubscriptionId: "",
        seatLimits: null,
      });
      setFeatureFlagsReady(true);
      return;
    }

    let cancelled = false;
    setFeatureFlagsReady(false);

    const loadCompanyFeatures = async () => {
      try {
        const [companySnap, globalControlsSnap] = await Promise.all([
          activeCompanyId ? getDoc(doc(db, "companies", activeCompanyId)) : Promise.resolve(null),
          getDoc(doc(db, "platformSettings", "developerControls")),
        ]);
        if (cancelled) return;
        const companyData = companySnap && companySnap.exists() ? companySnap.data() || {} : {};
        const globalData = globalControlsSnap.exists() ? globalControlsSnap.data() || {} : {};
        setCompanyFeatureFlags(companyData.featureFlags || {});
        setDeveloperFeatureFlags(globalData.featureFlags || {});
        setCompanyBillingState({
          planType: companyData.planType || null,
          subscriptionActive:
            typeof companyData.subscriptionActive === "boolean"
              ? companyData.subscriptionActive
              : null,
          stripeSubscriptionId: companyData.stripeSubscriptionId || "",
          seatLimits: companyData.seatLimits || null,
        });
        setFeatureFlagsReady(true);
      } catch (err) {
        console.warn("[App] Failed to load company feature flags:", err);
        if (!cancelled) {
          setCompanyFeatureFlags({});
          setDeveloperFeatureFlags({});
          setCompanyBillingState({
            planType: null,
            subscriptionActive: null,
            stripeSubscriptionId: "",
            seatLimits: null,
          });
          setFeatureFlagsReady(true);
        }
      }
    };

    loadCompanyFeatures();
    return () => {
      cancelled = true;
    };
  }, [user, userProfile?.companyId]);

  useEffect(() => {
    if (!user || !userProfile?.companyId) return undefined;

    const trimRecentErrors = () => {
      const now = Date.now();
      for (const [key, ts] of recentClientErrorKeysRef.current.entries()) {
        if (now - ts > 30000) recentClientErrorKeysRef.current.delete(key);
      }
    };

    const logClientError = async ({
      category = "runtime",
      severity = "error",
      message,
      stack = null,
      context = {},
    }) => {
      if (!message || !auth.currentUser) return;

      trimRecentErrors();
      const dedupeKey = `${category}:${message}:${stack || ""}`.slice(0, 500);
      if (recentClientErrorKeysRef.current.has(dedupeKey)) return;
      recentClientErrorKeysRef.current.set(dedupeKey, Date.now());

      try {
        const authToken = await auth.currentUser.getIdToken();
        await postFunctionJson(CLIENT_ERROR_LOG_ENDPOINT, {
          authToken,
          body: {
            authToken,
            companyId: userProfile.companyId,
            source: "web-app",
            category,
            severity,
            message,
            stack,
            context,
            url: window.location.href,
          },
        });
      } catch (err) {
        console.warn("[App] Failed to send client error log:", err);
      }
    };

    const onWindowError = (event) => {
      const message = event?.message || event?.error?.message || "Unhandled window error";
      const stack = event?.error?.stack || null;
      logClientError({
        category: "runtime",
        severity: "error",
        message,
        stack,
        context: {
          filename: event?.filename || null,
          lineno: event?.lineno || null,
          colno: event?.colno || null,
        },
      });
    };

    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      const message =
        reason?.message ||
        (typeof reason === "string" ? reason : "Unhandled promise rejection");
      const stack = reason?.stack || null;
      logClientError({
        category: "api",
        severity: "error",
        message,
        stack,
        context: {
          type: "unhandledrejection",
        },
      });
    };

    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [user, userProfile?.companyId]);

  const profileSufficient = useMemo(() => {
    if (!user) return true;
    if (!userProfile) return false;

    const hasMeaningfulFields =
      !!userProfile.role ||
      !!userProfile.companyId ||
      !!userProfile.planType ||
      typeof userProfile.companySetupCompleted === "boolean" ||
      typeof userProfile.setupCompleted === "boolean" ||
      !!userProfile.trialEndsAt ||
      typeof userProfile.subscriptionActive === "boolean";

    return hasMeaningfulFields;
  }, [user, userProfile]);

  const flags = useMemo(() => deriveUserFlags(user, userProfile), [user, userProfile]);
  const effectiveFeatureFlags = useMemo(
    () => mergeFeatureFlags(companyFeatureFlags, developerFeatureFlags),
    [companyFeatureFlags, developerFeatureFlags]
  );
  const featureAccessMap = useMemo(
    () =>
      getFeatureAccessMap({
        featureFlags: effectiveFeatureFlags,
        userEmail: user?.email || "",
        planType: companyBillingState.planType || userProfile?.planType,
        subscriptionActive:
          typeof companyBillingState.subscriptionActive === "boolean"
            ? companyBillingState.subscriptionActive
            : userProfile?.subscriptionActive === true,
        stripeSubscriptionId: companyBillingState.stripeSubscriptionId || "",
        seatLimits: companyBillingState.seatLimits || null,
      }),
    [
      effectiveFeatureFlags,
      user?.email,
      userProfile?.planType,
      userProfile?.subscriptionActive,
      companyBillingState.planType,
      companyBillingState.subscriptionActive,
      companyBillingState.stripeSubscriptionId,
      companyBillingState.seatLimits,
    ]
  );
  const ordersFeatureAccess = featureAccessMap.orders || { allowed: true, locked: false };
  const mapFeatureAccess = featureAccessMap.map || { allowed: true, locked: false };
  const gridsFeatureAccess = featureAccessMap.grids || { allowed: true, locked: false };
  const gridsRoleAllowed = canViewGrids({
    role: userProfile?.role || "",
    featureFlags: companyFeatureFlags || {},
  });

  // ✅ Only destructure what we actually use (prevents no-unused-vars)
  const {
    isCustomer,
    isAdminUser,
    defaultRoute,
    companyId,
    mustProvideCompanyInfo,
    mustChoosePlan,
    mustUpgradeForExpiredTrial,
  } = flags;

  // ✅ Determine if current route allows unverified access
  const allowUnverifiedHere = isAllowedUnverifiedRoute(path);

  // ✅ Determine gateTo based on verification status
  const gateTo = useMemo(() => {
    if (!user) return null;
    if (mustVerifyEmailAuthOnly(user) && !allowUnverifiedHere) return "/verify-email";
    return null;
  }, [user, allowUnverifiedHere]);

  const isAuthUIRoute =
    path.startsWith("/login") ||
    path.startsWith("/signup") ||
    path.startsWith("/invited-user") ||
    path.startsWith("/verify-email") ||
    path.startsWith("/sample-feedback");

  const shouldShowOverlayRaw = useMemo(() => {
    if (!user) return false;
    if (gateTo) return false;
    if (isAuthUIRoute) return false;
    return initializing || !profileSufficient;
  }, [user, gateTo, isAuthUIRoute, initializing, profileSufficient]);

  useEffect(() => {
    const now = Date.now();
    const minHoldMs = 450;

    if (shouldShowOverlayRaw) {
      overlayHoldUntilRef.current = Math.max(overlayHoldUntilRef.current, now + minHoldMs);
      if (!overlayVisible) setOverlayVisible(true);
      return;
    }

    const remaining = overlayHoldUntilRef.current - now;
    if (remaining > 0) {
      const t = setTimeout(() => setOverlayVisible(false), remaining);
      return () => clearTimeout(t);
    }

    if (overlayVisible) setOverlayVisible(false);
  }, [shouldShowOverlayRaw, overlayVisible]);

  const DEV_EMAILS = ["roserobin13@gmail.com"];
  const isDev = !!user && DEV_EMAILS.includes((user.email || "").toLowerCase());

  const redirectAfterLogin = () => {
    if (!user) return "/login";

    // ✅ Check verification first
    if (mustVerifyEmailAuthOnly(user)) {
      return "/verify-email";
    }

    if (gateTo) return gateTo;
    if (!profileSufficient) return "/";

    return getPostAuthRoute(flags);
  };

  const requireSetupOrUpgrade = (elementForAdmins, elementForCustomer) => {
    if (!user) return <Navigate to="/login" replace />;

    // ✅ Block unverified users
    if (mustVerifyEmailAuthOnly(user)) {
      return <Navigate to="/verify-email" replace />;
    }

    if (gateTo) return <Navigate to={gateTo} replace />;
    if (!profileSufficient) return <div className="min-h-screen bg-slate-950" />;
    if (!featureFlagsReady) return <div className="min-h-screen bg-slate-950" />;

    if (!isAdminUser) return elementForCustomer ?? elementForAdmins;

    if (mustProvideCompanyInfo) return <Navigate to="/complete-account-setup" replace />;
    if (mustUpgradeForExpiredTrial) return <Navigate to="/complete-account-setup?mode=upgrade" replace />;
    if (mustChoosePlan) return <Navigate to="/complete-account-setup?mode=upgrade" replace />;

    return elementForAdmins;
  };

  return (
    <React.Fragment>
      {overlayVisible && <BootOverlay message="Loading your account…" />}

      <Routes>
        <Route
          path="/"
          element={
            user ? (
              initializing ? (
                <RouteBootShell />
              ) : gateTo ? (
                <Navigate to={gateTo} replace />
              ) : !profileSufficient ? (
                <RouteBootShell />
              ) : (
                <Navigate to={redirectAfterLogin()} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/login"
          element={
            <LoginWithVerificationHandler
              user={user}
              redirectAfterLogin={redirectAfterLogin}
              gateTo={gateTo}
              profileSufficient={profileSufficient}
              initializing={initializing}
            />
          }
        />

        <Route
          path="/verify-email"
          element={
            <VerifyEmailRoute
              user={user}
              redirectAfterLogin={redirectAfterLogin}
              gateTo={gateTo}
              profileSufficient={profileSufficient}
              initializing={initializing}
            />
          }
        />

        <Route
          path="/signup"
          element={user && !gateTo ? <Navigate to={redirectAfterLogin()} replace /> : <SignUp />}
        />

        <Route path="/sample-feedback" element={<FeedbackForm />} />
        <Route path="/invited-user" element={<InvitedUserDetails />} />

        <Route path="/complete-setup" element={<CompleteAccountSetupRoute user={user} gateTo={gateTo} initializing={initializing} />} />
        <Route
          path="/complete-account-setup"
          element={<CompleteAccountSetupRoute user={user} gateTo={gateTo} initializing={initializing} />}
        />

        {/* ✅ PROTECTED ROUTES - Require verification for ALL app routes */}
        <Route
          element={
            <RequireAuth user={user} gateTo={gateTo} initializing={initializing}>
              {/* ✅ FIX: Don't mount NotificationsProvider/AppLayout until profile is sufficient.
                  This avoids providers/listeners running with companyId=null during first-login races. */}
              {profileSufficient ? (
                <NotificationsProvider currentUserId={user?.uid || null} companyId={companyId || null}>
                  <AppLayout />
                </NotificationsProvider>
              ) : (
                <div className="min-h-screen bg-slate-950" />
              )}
            </RequireAuth>
          }
        >
          <Route
            path="/dashboard"
            element={requireSetupOrUpgrade(
              isCustomer ? (
                <Navigate to="/customer" replace />
              ) : (
                <Dashboard companyId={companyId} me={userProfile} firebaseUser={user} />
              ),
              <Dashboard companyId={companyId} me={userProfile} firebaseUser={user} />
            )}
          />

          <Route
            path="/sample-orders"
            element={requireSetupOrUpgrade(
              isCustomer ? <Navigate to="/customer" replace /> : <SampleOrdersScreen />,
              <SampleOrdersScreen />
            )}
          />

          <Route
            path="/orders"
            element={
              ordersFeatureAccess.allowed
                ? requireSetupOrUpgrade(
                    isCustomer ? <Navigate to="/customer" replace /> : <OrdersScreen />,
                    <OrdersScreen />
                  )
                : <Navigate to={defaultRoute} replace />
            }
          />

          <Route
            path="/accounts"
            element={requireSetupOrUpgrade(
              isCustomer ? <Navigate to="/customer" replace /> : <AccountsScreen />,
              <AccountsScreen />
            )}
          />

          <Route
            path="/inventory"
            element={requireSetupOrUpgrade(
              isCustomer ? <Navigate to="/customer" replace /> : <InventoryScreen />,
              <InventoryScreen />
            )}
          />

          <Route
            path="/territories-map"
            element={
              mapFeatureAccess.allowed
                ? requireSetupOrUpgrade(
                    isCustomer ? <Navigate to="/customer" replace /> : <TerritoriesMapScreen />,
                    <TerritoriesMapScreen />
                  )
                : <Navigate to={defaultRoute} replace />
            }
          />

          <Route
            path="/tasks"
            element={requireSetupOrUpgrade(
              isCustomer ? <Navigate to="/customer" replace /> : <TaskScreen companyId={companyId} me={userProfile} />,
              <TaskScreen companyId={companyId} me={userProfile} />
            )}
          />

          <Route
            path="/grids"
            element={
              gridsRoleAllowed && gridsFeatureAccess.allowed
                ? requireSetupOrUpgrade(
                    isCustomer ? <Navigate to="/customer" replace /> : <GridsScreen />,
                    <GridsScreen />
                  )
                : <Navigate to={defaultRoute} replace />
            }
          />

          {/* ✅ NEW: Documents route */}
          <Route
            path="/documents"
            element={requireSetupOrUpgrade(
              isCustomer ? <Navigate to="/customer" replace /> : <DocumentsScreen companyId={companyId} userProfile={userProfile} />,
              <DocumentsScreen companyId={companyId} userProfile={userProfile} />
            )}
          />

          <Route
            path="/settings"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : gateTo ? (
                <Navigate to={gateTo} replace />
              ) : !profileSufficient ? (
                <div className="min-h-screen bg-slate-950" />
              ) : isCustomer ? (
                <Navigate to={defaultRoute} replace />
              ) : (
                requireSetupOrUpgrade(<ProfileScreen />, <ProfileScreen />)
              )
            }
          />

          <Route
            path="/team"
            element={requireSetupOrUpgrade(isCustomer ? <Navigate to="/customer" replace /> : <TeamScreen />, <TeamScreen />)}
          />

          <Route
            path="/users"
            element={requireSetupOrUpgrade(
              isCustomer ? <Navigate to="/customer" replace /> : <UsersScreen />,
              <UsersScreen />
            )}
          />

          <Route
            path="/notifications"
            element={requireSetupOrUpgrade(
              <NotificationsScreen companyId={companyId} />,
              <NotificationsScreen companyId={companyId} />
            )}
          />

          <Route
            path="/admin"
            element={
              !profileSufficient ? (
                <div className="min-h-screen bg-slate-950" />
              ) : isAdminUser ? (
                requireSetupOrUpgrade(<AdminScreen />, <AdminScreen />)
              ) : (
                <Navigate to={defaultRoute} replace />
              )
            }
          />

          <Route
            path="/dev-tools"
            element={
              !profileSufficient ? (
                <div className="min-h-screen bg-slate-950" />
              ) : isDev ? (
                requireSetupOrUpgrade(<DeveloperToolsScreen />, <DeveloperToolsScreen />)
              ) : (
                <Navigate to={defaultRoute} replace />
              )
            }
          />

          <Route
            path="/customer"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : gateTo ? (
                <Navigate to={gateTo} replace />
              ) : !profileSufficient ? (
                <div className="min-h-screen bg-slate-950" />
              ) : isCustomer ? (
                <CustomerSampleScreen />
              ) : (
                <Navigate to={defaultRoute} replace />
              )
            }
          />
        </Route>

        <Route
          path="*"
          element={
            user ? (
              gateTo ? (
                <Navigate to={gateTo} replace />
              ) : !profileSufficient ? (
                <div className="min-h-screen bg-slate-950" />
              ) : (
                <Navigate to={redirectAfterLogin()} replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </React.Fragment>
  );
}

export default App;
